import { createAsyncThunk } from '@reduxjs/toolkit';
import chevron from '@/services/chevron';
import type { Domain, DomainListItem } from './types';
import { showToast } from '../notifications/slice';
import { selectUser } from '../user/selectors';
import type { RootState } from '../store';
import { setSelectedDomain } from './slice';
import { selectCurrentPage } from '../pages/selectors';
import { selectSelectedDomain } from './selectors';

export const DOMAINS_PATH = 'my-account/domains/';

/**
 * Fetches the custom domains for the current user.
 *
 * TODO: Revaluate this thunk and decide how we want to cohesively handle app bootstrapping
 * of all required data.
 * This thunk is heavily dependent on user fetching, dependencies need to be ironed out better.
 */
const mapDomainStatus = (d: Domain): DomainListItem => {
  if (d.status === 'secured') {
    return { domain: d.domain, chipStatus: 'connected', isSelectable: true };
  }
  if (d.status === 'connected' || d.status === 'pending') {
    return { domain: d.domain, chipStatus: 'pending', isSelectable: false };
  }
  return { domain: d.domain, chipStatus: 'error', isSelectable: false };
};

export const fetchDomains = createAsyncThunk<DomainListItem[], void>(
  'domains/fetchDomains',
  async (_, { dispatch, getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const user = selectUser(state);
    const lpSubDomain = user?.subDomain;
    const lpDefault: DomainListItem | null = lpSubDomain
      ? {
          domain: `${lpSubDomain}.${import.meta.env.VITE_LP_DEFAULT_DOMAIN}`,
          chipStatus: 'default',
          isSelectable: true,
        }
      : null;

    try {
      const response = (await chevron.http({
        url: `${import.meta.env.VITE_API_MAIN_APP}/${DOMAINS_PATH}`,
        method: 'GET',
      })) as { domains: Domain[] };

      const customDomains = response.domains.map(mapDomainStatus);

      const domainList: DomainListItem[] = [...(lpDefault ? [lpDefault] : []), ...customDomains];

      // Auto-select a default domain if none is currently selected
      const currentState = getState() as RootState;
      const currentPage = selectCurrentPage(currentState);
      const currentSelectedDomain = selectSelectedDomain(currentState);

      if (!currentSelectedDomain && domainList.length > 0) {
        // If the page already has a domain, use that; otherwise use the first domain (LP domain)
        const defaultDomain = currentPage?.domain || domainList[0].domain;
        // Only set if the domain exists in the list
        const match = domainList.find((d) => d.domain === defaultDomain);
        if (match) {
          dispatch(setSelectedDomain(match.domain));
        } else if (domainList.length > 0) {
          dispatch(setSelectedDomain(domainList[0].domain));
        }
      }

      return domainList;
    } catch (e) {
      console.warn(e);
      dispatch(showToast({ text: 'Failed to fetch domains', state: 'warning' }));
      return rejectWithValue('Failed to fetch domains');
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState() as RootState;
      // Don't fetch if already loading or if we've already attempted a fetch
      if (state.domains.loading || state.domains.hasAttemptedFetch) {
        return false;
      }
      // Don't fetch if user is still loading (needed for lpDomain)
      // If we don't wait for the user, they will never get their lpDomain, and are left with nothing.
      if (state.user.loading || !state.user.data) {
        return false;
      }
      return true;
    },
  },
);
